import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  archiveAdminTemplate,
  createAdminTemplate,
  listAdminTemplates,
  publishAdminTemplate,
  type TemplateDomain,
  type TemplateRecord,
  type TemplateStatus,
  type TemplateTaskType,
} from '@/services/templateSystemApi';
import { queryKeys } from '@/lib/queryKeys';
import { getUserFriendlyMessage } from '@/services/apiError';
import FormMessage from '@/components/common/FormMessage';
import ContentApiErrorState from '@/components/common/ContentErrorState';

const domainOptions: Array<{ label: string; value: TemplateDomain | 'all' }> = [
  { label: '全部学科', value: 'all' },
  { label: 'Python', value: 'python' },
  { label: '信息系统', value: 'information_systems' },
  { label: '网络', value: 'networks' },
  { label: '物联网', value: 'iot' },
];

const taskOptions: Array<{ label: string; value: TemplateTaskType | 'all' }> = [
  { label: '全部任务', value: 'all' },
  { label: '教案', value: 'lesson_plan' },
  { label: '题集', value: 'question_set' },
  { label: '拓扑', value: 'topology' },
  { label: '课件', value: 'slides' },
  { label: '混合', value: 'mixed' },
];

const statusOptions: Array<{ label: string; value: TemplateStatus | 'all' }> = [
  { label: '全部状态', value: 'all' },
  { label: '草稿', value: 'draft' },
  { label: '已发布', value: 'published' },
  { label: '已归档', value: 'archived' },
];

export default function AdminTemplatesPage() {
  const [domain, setDomain] = useState<TemplateDomain | 'all'>('all');
  const [taskType, setTaskType] = useState<TemplateTaskType | 'all'>('all');
  const [status, setStatus] = useState<TemplateStatus | 'all'>('all');
  const [keyword, setKeyword] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [workingId, setWorkingId] = useState<string | null>(null);

  const filters = useMemo(
    () => ({
      domain: domain === 'all' ? undefined : domain,
      taskType: taskType === 'all' ? undefined : taskType,
      status: status === 'all' ? undefined : status,
      keyword: keyword.trim() || undefined,
    }),
    [domain, taskType, status, keyword],
  );

  const templatesQuery = useQuery({
    queryKey: queryKeys.templates.adminList(filters),
    queryFn: () => listAdminTemplates(filters),
    retry: 1,
  });

  const handlePublish = async (template: TemplateRecord) => {
    const accepted = window.confirm(`确认发布模板「${template.name}」吗？`);
    if (!accepted) {
      return;
    }

    setActionError('');
    setActionSuccess('');
    setWorkingId(template.id);
    try {
      const result = await publishAdminTemplate(template.id);
      setActionSuccess(`已发布：版本 ${result.version.version}`);
      await templatesQuery.refetch();
    } catch (error) {
      setActionError(getUserFriendlyMessage(error, '发布失败'));
    } finally {
      setWorkingId(null);
    }
  };

  const handleArchive = async (template: TemplateRecord) => {
    const accepted = window.confirm(`确认归档模板「${template.name}」吗？`);
    if (!accepted) {
      return;
    }

    setActionError('');
    setActionSuccess('');
    setWorkingId(template.id);
    try {
      await archiveAdminTemplate(template.id);
      setActionSuccess('模板已归档');
      await templatesQuery.refetch();
    } catch (error) {
      setActionError(getUserFriendlyMessage(error, '归档失败'));
    } finally {
      setWorkingId(null);
    }
  };

  const handleDuplicate = async (template: TemplateRecord) => {
    setActionError('');
    setActionSuccess('');
    setWorkingId(template.id);
    try {
      const suffix = new Date().getTime().toString().slice(-5);
      await createAdminTemplate({
        ...template,
        name: `${template.name} (Copy)`,
        slug: `${template.slug}-copy-${suffix}`,
        status: 'draft',
      });
      setActionSuccess('模板已复制为草稿');
      await templatesQuery.refetch();
    } catch (error) {
      setActionError(getUserFriendlyMessage(error, '复制失败'));
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <div className='max-w-[1180px] mx-auto px-6 py-12'>
      <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7'>
        <div>
          <h1
            className='font-semibold text-apple-near-black mb-2'
            style={{ fontSize: '2rem', lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            模板管理
          </h1>
          <p className='text-apple-gray text-[0.9375rem]'>管理可复用的教学生成模板并发布版本。</p>
        </div>
        <Link to='/admin/templates/new' className='btn btn-primary'>
          新建模板
        </Link>
      </div>

      {(actionError || actionSuccess) && (
        <div className='space-y-2 mb-4'>
          {actionError ? <FormMessage tone='error' message={actionError} /> : null}
          {actionSuccess ? <FormMessage tone='success' message={actionSuccess} /> : null}
        </div>
      )}

      <div className='bg-white rounded-2xl p-4 shadow-card-sm mb-5 grid grid-cols-1 md:grid-cols-4 gap-3'>
        <input
          className='input'
          placeholder='搜索名称/slug'
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
        />

        <select className='input' value={domain} onChange={(event) => setDomain(event.target.value as TemplateDomain | 'all')}>
          {domainOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select className='input' value={taskType} onChange={(event) => setTaskType(event.target.value as TemplateTaskType | 'all')}>
          {taskOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <select className='input' value={status} onChange={(event) => setStatus(event.target.value as TemplateStatus | 'all')}>
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <section className='bg-white rounded-2xl shadow-card-sm overflow-hidden'>
        {templatesQuery.isLoading ? <p className='p-6 text-apple-gray'>加载中…</p> : null}
        {templatesQuery.isError ? (
          <div className='p-6'>
            <ContentApiErrorState error={templatesQuery.error} onRetry={() => void templatesQuery.refetch()} />
          </div>
        ) : null}

        {!templatesQuery.isLoading && !templatesQuery.isError ? (
          <div className='overflow-x-auto'>
            <table className='min-w-full text-[0.875rem]'>
              <thead>
                <tr className='text-left border-b border-apple-border bg-apple-light-gray/60'>
                  <th className='px-4 py-3 font-medium'>名称</th>
                  <th className='px-4 py-3 font-medium'>领域</th>
                  <th className='px-4 py-3 font-medium'>任务</th>
                  <th className='px-4 py-3 font-medium'>状态</th>
                  <th className='px-4 py-3 font-medium'>版本</th>
                  <th className='px-4 py-3 font-medium'>操作</th>
                </tr>
              </thead>
              <tbody>
                {(templatesQuery.data ?? []).map((template) => (
                  <tr key={template.id} className='border-b border-apple-border/70 align-top'>
                    <td className='px-4 py-3'>
                      <div className='font-medium text-apple-near-black'>{template.name}</div>
                      <div className='text-apple-gray text-[0.75rem] mt-1'>{template.slug}</div>
                    </td>
                    <td className='px-4 py-3 text-apple-gray'>{template.domain}</td>
                    <td className='px-4 py-3 text-apple-gray'>{template.taskType}</td>
                    <td className='px-4 py-3 text-apple-gray'>{template.status}</td>
                    <td className='px-4 py-3 text-apple-gray'>v{template.version}</td>
                    <td className='px-4 py-3'>
                      <div className='flex flex-wrap gap-2'>
                        <Link to={`/admin/templates/${template.id}`} className='btn btn-secondary'>
                          编辑
                        </Link>
                        <button
                          type='button'
                          className='btn btn-secondary'
                          disabled={workingId === template.id}
                          onClick={() => void handlePublish(template)}
                        >
                          发布
                        </button>
                        <button
                          type='button'
                          className='btn btn-secondary'
                          disabled={workingId === template.id}
                          onClick={() => void handleArchive(template)}
                        >
                          归档
                        </button>
                        <button
                          type='button'
                          className='btn btn-secondary'
                          disabled={workingId === template.id}
                          onClick={() => void handleDuplicate(template)}
                        >
                          复制
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}
